import React from 'react';

import { Box, Card, Typography } from '@mui/material';

import { ActionButtons } from './components/ActionButtons';
import { LinkContent } from './components/LinkContent';
import { LinkInfo } from './components/LinkInfo';
import { LinkTypeSelector } from './components/LinkTypeSelector';
import { ScreenshotPreview } from './components/ScreenshotPreview';

import type { LinkRendererViewProperties } from './LinkRendererView.types';

export const LinkRendererView: React.FC<LinkRendererViewProperties> = ({
  url = '',
  isPending,
  isDisabled,
  isDeletable,
  screenshotUrl = '',
  title = '',
  description = '',
  urlNormalised = '',
  linkTypeDisplayValue = '',
  linkTypeChoices,
  onTypeChange,
  onDelete,
  dataTest = 'link_renderer_view',
}) => (
  <Box
    data-testid={dataTest}
    sx={{
      display: 'flex',
      flexDirection: 'column'
    }}>
    <Card
      data-testid={`${dataTest}_card`}
      variant='outlined'
      sx={{
        position: 'relative',
        borderRadius: '8px',
        overflow: 'hidden',
        transition: 'box-shadow 0.2s',
        '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
      }}
    >
      <LinkContent url={url} isDisabled={isDisabled}>
        <ScreenshotPreview
          isPending={isPending}
          screenshotUrl={screenshotUrl}
          url={url}
          urlNormalised={urlNormalised}
          dataTest={`${dataTest}_screenshot`}
        />
      </LinkContent>

      <Box data-testid={`${dataTest}_body`} sx={{ px: 2, py: 1.5 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start'
          }}>
          <LinkInfo
            isPending={isPending}
            title={title}
            url={url}
            description={description}
            dataTest={`${dataTest}_info`}
          />
          {isDeletable && !isDisabled && (
            <ActionButtons
              url={url}
              onDelete={onDelete}
              dataTest={`${dataTest}_actions`}
            />
          )}
        </Box>
        <Box
          data-testid={`${dataTest}_footer`}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 1
          }}>
          <Typography
            data-testid={`${dataTest}_url`}
            sx={{
              fontSize: '11px',
              color: 'text.secondary'
            }}>
            {urlNormalised}
          </Typography>
          <LinkTypeSelector
            linkTypeDisplayValue={linkTypeDisplayValue}
            linkTypeChoices={linkTypeChoices}
            onTypeChange={onTypeChange}
            dataTest={`${dataTest}_type`}
          />
        </Box>
      </Box>
    </Card>
  </Box>
);
